import React, { useEffect, useState } from 'react'
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  LinkOutlined,
  MobileOutlined,
  PlusOutlined
} from '@ant-design/icons'
import { Button, Card, Collapse, Form, Input, Modal, notification, Space } from 'antd'
import idx from 'idx'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Redirect, Route } from 'react-router-dom'

import {
  fetchVideoContentById,
  removeVideoContent,
  resetVideoContentActionStatus,
  updateVideoContent
} from '~actions'
import { ResultModal } from '~components'
import { getCurrentUser } from '~context'
import { PageLayout, Spinner } from '~stories'

import styles from './EditVideoContent.module.css'

const { Panel } = Collapse
const NOTIFICATION_MESSAGES = {
  UNAUTHORIZED: 'You are not authorized to perform this action.'
}

const URL_RULE = {
  message: 'Please enter a valid URL starting with http:// or https://',
  pattern: /^https?:\/\/.+/
}

const isEmbedUrl = url => /player\.vimeo\.com|youtube\.com\/embed|youtu\.be/.test(url)

const VideoPreview = ({ url }) => {
  if (isEmbedUrl(url)) {
    return <iframe allowFullScreen className={styles.videoIframe} src={url} title="Video preview" />
  }
  return (
    <video className={styles.videoPlayer} controls muted src={url}>
      Your browser does not support the video tag.
    </video>
  )
}

VideoPreview.propTypes = {
  url: PropTypes.string.isRequired
}

const EditVideoContent = ({
  deleteMessage,
  deleteStatus,
  fetchVideoContentById,
  removeVideoContent,
  resetVideoContentActionStatus,
  route,
  updateMessage,
  updateStatus,
  updateVideoContent,
  videoContentItem
}) => {
  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [videoContentId, setVideoContentId] = useState('')
  const { access_token: accessToken, userDetails: { isContentEditor } } = getCurrentUser()
  const [form] = Form.useForm()

  useEffect(() => {
    const id = idx(route, _ => _.match.params.id)
    if (id) {
      setIsDataLoaded(false)
      fetchVideoContentById(id)
      setVideoContentId(id)
    }
  }, [route, fetchVideoContentById])

  useEffect(() => {
    if (videoContentItem) {
      form.setFieldsValue({
        pageName: videoContentItem.pageName,
        videos: videoContentItem.videos || []
      })
      setIsDataLoaded(true)
    }
  }, [videoContentItem, form])

  useEffect(() => {
    if (updateStatus && updateMessage) {
      setIsDataLoaded(true)
    } else if (!updateStatus && updateMessage) {
      setIsDataLoaded(true)
      notification.error({ message: updateMessage })
      resetVideoContentActionStatus()
    }
  }, [updateStatus, updateMessage, resetVideoContentActionStatus])

  useEffect(() => {
    if (deleteStatus && deleteMessage) {
      setIsDataLoaded(true)
      notification.success({ message: deleteMessage })
      resetVideoContentActionStatus()
    } else if (!deleteStatus && deleteMessage) {
      setIsDataLoaded(true)
      notification.error({ message: deleteMessage })
    }
  }, [deleteStatus, deleteMessage, resetVideoContentActionStatus])

  const hasDuplicateUrls = (videos) => {
    const urls = (videos || []).map(v => (v?.url || '').trim().toLowerCase()).filter(Boolean)
    return urls.length !== new Set(urls).size
  }

  const handleSubmit = () => {
    form.validateFields()
      .then(values => {
        if (!isContentEditor) {
          notification.error({ message: NOTIFICATION_MESSAGES.UNAUTHORIZED })
          return
        }
        if (hasDuplicateUrls(values.videos)) {
          notification.error({ message: 'Duplicate video URLs are not allowed. Please use unique URLs.' })
          return
        }
        setIsDataLoaded(false)
        updateVideoContent(videoContentId, { pageName: values.pageName, videos: values.videos }, accessToken)
      })
      .catch(() => {
        notification.warning({ message: 'Please fill in all required fields before submitting.' })
      })
  }

  const handleAddVideo = addFn => {
    form.validateFields()
      .then(() => addFn())
      .catch(() => {
        notification.warning({ message: 'Please fill in all required fields before adding a new video.' })
      })
  }

  const handleDelete = () => {
    if (!isContentEditor) {
      notification.error({ message: NOTIFICATION_MESSAGES.UNAUTHORIZED })
      return
    }
    Modal.confirm({
      cancelText: 'Cancel',
      content: 'This video content entry will be removed permanently. Are you sure?',
      okText: 'Yes, Delete',
      okType: 'danger',
      onOk: () => {
        setIsDataLoaded(false)
        removeVideoContent(videoContentId, accessToken)
      },
      title: 'Delete Video Content'
    })
  }

  const confirmRemoveVideo = (e, removeFn, index) => {
    e.stopPropagation()
    Modal.confirm({
      cancelText: 'Cancel',
      content: `Video ${index + 1} and its URLs will be removed from this entry.`,
      okText: 'Remove',
      okType: 'danger',
      onOk: () => removeFn(index),
      title: 'Remove this video?'
    })
  }

  const handleModalClose = () => {
    resetVideoContentActionStatus()
  }

  const keepEditingButton = (
    <Route key="editVideoContent" render={({ history }) => (
      <Button
        onClick={() => {
          resetVideoContentActionStatus()
          history.push(`/video-contents/${videoContentId}/edit`)
        }}
        type="primary"
      >
        Keep Editing
      </Button>
    )} />
  )

  const goToListButton = (
    <Route key="listVideoContents" render={({ history }) => (
      <Button
        onClick={() => {
          resetVideoContentActionStatus()
          history.push('/video-contents')
        }}
        type="primary"
      >
        Go to Video Contents List
      </Button>
    )} />
  )

  const deleteButton = (
    <Button danger onClick={handleDelete} type="default">
      Delete
    </Button>
  )

  return (
    <PageLayout extra={[deleteButton]} title="Edit Video Content">
      <div className={deleteStatus ? styles.blurred : ''}>
        <div className={styles.pageContainer}>
          <Card className={styles.formCard}>
            <Form
              form={form}
              layout="vertical"
            >
              <Form.Item
                label="Page Name"
                name="pageName"
                rules={[
                  { required: true, message: 'Please enter a page name' }
                ]}
              >
                <Input disabled size="large" />
              </Form.Item>

              <Form.List
                name="videos"
                rules={[{
                  validator: async (_, items) => {
                    if (!items || items.length < 1) {
                      return Promise.reject(new Error('At least one video is required'))
                    }
                  }
                }]}
              >
                {(fields, { add, move, remove }, { errors }) => (
                  <Card
                    className={styles.videosCard}
                    extra={
                      <Button
                        icon={<PlusOutlined />}
                        onClick={(e) => { e.stopPropagation(); handleAddVideo(add) }}
                        shape="circle"
                        type="primary"
                      />
                    }
                    title="Video Contents"
                  >
                    <Form.Item noStyle shouldUpdate>
                      {() => {
                        const allVideos = form.getFieldValue('videos') || []
                        return (
                          <Collapse accordion defaultActiveKey={['0']} expandIconPosition="right">
                            {fields.map(({ key, name: fieldName, ...restField }) => {
                              const video = allVideos[fieldName] || {}
                              const videoName = video.name || `Video ${fieldName + 1}`
                              const url = video.url || ''
                              const hasValidUrl = url && URL_RULE.pattern.test(url)
                              return (
                                <Panel
                                  key={String(key)}
                                  extra={
                                    <Space size={6}>
                                      {fields.length > 1 && fieldName !== 0 && (
                                        <Button
                                          className={styles.primaryButton}
                                          onClick={(e) => { e.stopPropagation(); move(fieldName, fieldName - 1) }}
                                          size="small"
                                          type="primary"
                                        >
                                          <ArrowUpOutlined />
                                        </Button>
                                      )}
                                      {fields.length > 1 && fieldName !== fields.length - 1 && (
                                        <Button
                                          className={styles.primaryButton}
                                          onClick={(e) => { e.stopPropagation(); move(fieldName, fieldName + 1) }}
                                          size="small"
                                          type="primary"
                                        >
                                          <ArrowDownOutlined />
                                        </Button>
                                      )}
                                      {fields.length > 1 && (
                                        <Button
                                          className={styles.deleteButton}
                                          danger
                                          onClick={(e) => confirmRemoveVideo(e, remove, fieldName)}
                                          size="small"
                                          type="primary"
                                        >
                                  Delete
                                        </Button>
                                      )}
                                    </Space>
                                  }
                                  header={
                                    <div className={styles.panelHeader}>
                                      {hasValidUrl && (
                                        isEmbedUrl(url)
                                          ? <div className={styles.panelThumbnailEmbed}><iframe src={url} title={videoName} /></div>
                                          : <video className={styles.panelThumbnail} muted onLoadedMetadata={e => { e.target.currentTime = e.target.duration / 2 }} preload="metadata" src={url} />
                                      )}
                                      <span className={styles.panelTitle}>{videoName}</span>
                                    </div>
                                  }
                                >
                                  <Form.Item
                                    {...restField}
                                    label={<span className={styles.fieldLabel}>Video Name</span>}
                                    name={[fieldName, 'name']}
                                    rules={[{ required: true, message: 'Please enter a video name' }]}
                                  >
                                    <Input placeholder="e.g. Hero Banner, Background Loop" />
                                  </Form.Item>
                                  <Form.Item
                                    {...restField}
                                    label={
                                      <span className={styles.fieldLabel}>
                                        <LinkOutlined /> Video URL
                                      </span>
                                    }
                                    name={[fieldName, 'url']}
                                    rules={[
                                      { required: true, message: 'Please enter the video URL' },
                                      URL_RULE
                                    ]}
                                  >
                                    <Input placeholder="https://cdn.example.com/videos/hero.mp4" />
                                  </Form.Item>
                                  <Form.Item noStyle shouldUpdate={(prev, cur) => {
                                    const prevUrl = prev?.videos?.[fieldName]?.url
                                    const curUrl = cur?.videos?.[fieldName]?.url
                                    return prevUrl !== curUrl
                                  }}>
                                    {() => {
                                      const url = form.getFieldValue(['videos', fieldName, 'url'])
                                      if (!url || !URL_RULE.pattern.test(url)) return null
                                      return (
                                        <div className={styles.videoPreview}>
                                          <div className={styles.videoPreviewLabel}>Preview</div>
                                          <VideoPreview url={url} />
                                        </div>
                                      )
                                    }}
                                  </Form.Item>
                                  <Form.Item
                                    {...restField}
                                    extra="Leave empty to use the same Video URL above"
                                    label={
                                      <span className={styles.fieldLabel}>
                                        <MobileOutlined /> Mobile Video URL
                                      </span>
                                    }
                                    name={[fieldName, 'mobileUrl']}
                                    rules={[{ ...URL_RULE, required: false }]}
                                  >
                                    <Input placeholder="https://cdn.example.com/videos/hero-mobile.mp4" />
                                  </Form.Item>
                                  <Form.Item noStyle shouldUpdate={(prev, cur) => {
                                    const prevUrl = prev?.videos?.[fieldName]?.mobileUrl
                                    const curUrl = cur?.videos?.[fieldName]?.mobileUrl
                                    return prevUrl !== curUrl
                                  }}>
                                    {() => {
                                      const mobileUrl = form.getFieldValue(['videos', fieldName, 'mobileUrl'])
                                      if (!mobileUrl || !URL_RULE.pattern.test(mobileUrl)) return null
                                      return (
                                        <div className={styles.videoPreview}>
                                          <div className={styles.videoPreviewLabel}>Mobile Preview</div>
                                          <VideoPreview url={mobileUrl} />
                                        </div>
                                      )
                                    }}
                                  </Form.Item>
                                </Panel>
                              )
                            })}
                          </Collapse>
                        )
                      }}
                    </Form.Item>
                    <Form.ErrorList errors={errors} />
                  </Card>
                )}
              </Form.List>

              <Form.Item>
                <Button className={styles.submitBtn} onClick={handleSubmit} type="primary">
                  Update
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </div>
        <Spinner isLoading={!isDataLoaded} />
      </div>
      {
        (updateStatus === true && updateMessage) &&
        <ResultModal
          actions={[
            goToListButton,
            keepEditingButton
          ]}
          handleCancel={handleModalClose}
          status="success"
          title={updateMessage}
        />
      }
      {deleteStatus && <Redirect to="/video-contents" />}
    </PageLayout>
  )
}

const mapStateToProps = ({ videoContent }) => ({
  deleteMessage: videoContent.deleteMessage,
  deleteStatus: videoContent.deleteStatus,
  updateMessage: videoContent.message,
  updateStatus: videoContent.success,
  videoContentItem: videoContent.videoContent
})

const mapDispatchToProps = {
  fetchVideoContentById,
  removeVideoContent,
  resetVideoContentActionStatus,
  updateVideoContent
}

EditVideoContent.propTypes = {
  deleteMessage: PropTypes.string,
  deleteStatus: PropTypes.bool,
  fetchVideoContentById: PropTypes.func,
  removeVideoContent: PropTypes.func,
  resetVideoContentActionStatus: PropTypes.func,
  route: PropTypes.object.isRequired,
  updateMessage: PropTypes.string,
  updateStatus: PropTypes.bool,
  updateVideoContent: PropTypes.func,
  videoContentItem: PropTypes.object
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EditVideoContent)
